import { useEffect, useRef, useState } from 'react'
import { Avatar } from '../components/Avatar'
import { VoiceControl } from '../components/VoiceControl'
import { useChatStore } from '../state/chatStore'
import { joinShowSession } from '../lib/api'

/** After this long without interaction the kiosk wipes the screen and starts over. */
const IDLE_RESET_MS = 90_000

/**
 * SPEC "Kiosk-Modus": big screen at the entrance with avatar and microphone.
 *
 * Two decisions worth stating outright:
 *
 * 1. **No camera-based presence detection.** The spec imagines K4ia greeting
 *    whoever steps in front of the screen, but a passer-by cannot consent to
 *    being filmed, and consent before camera use is a hard rule for this
 *    project. The kiosk therefore invites interaction instead of watching for
 *    it — a visible attract loop, and a greeting the moment someone touches or
 *    speaks to it. If J4F later adds a hardware proximity sensor, it can
 *    trigger `begin()` here without touching anything else.
 * 2. **Every visitor gets a fresh session.** This is a shared screen: the next
 *    person must never find the previous conversation on it. The screen wipes
 *    itself after ~90 seconds without interaction, and on demand.
 */
/** Randomly generated per screen, unguessable enough for a room where it is visible anyway. */
function makePairingCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' // no I/O/0/1 — read off a screen across a room
  return Array.from(
    crypto.getRandomValues(new Uint8Array(5)),
    (n) => alphabet[n % alphabet.length],
  ).join('')
}

/** How often the big screen re-reads the session the tablet is driving. */
const SHOW_POLL_MS = 2000

export function KioskView({ showMode = false }: { showMode?: boolean }) {
  const [active, setActive] = useState(false)
  const [showCode] = useState<string | null>(() => (showMode ? makePairingCode() : null))
  const messages = useChatStore((s) => s.messages)
  const avatarState = useChatStore((s) => s.avatarState)
  const micLevel = useChatStore((s) => s.micLevel)
  const init = useChatStore((s) => s.init)
  const reset = useChatStore((s) => s.reset)
  const sendMessage = useChatStore((s) => s.sendMessage)
  const refresh = useChatStore((s) => s.refresh)
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const lastAnswer = [...messages].reverse().find((m) => m.role === 'assistant')
  const lastQuestion = [...messages].reverse().find((m) => m.role === 'user')

  function touch() {
    if (idleTimer.current) clearTimeout(idleTimer.current)
    idleTimer.current = setTimeout(() => {
      reset()
      setActive(false)
    }, IDLE_RESET_MS)
  }

  async function begin() {
    setActive(true)
    touch()
    // A brand-new session per visitor, so nothing of the previous one leaks.
    await init('kiosk')
    await sendMessage('Hallo!')
  }

  useEffect(() => {
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current)
    }
  }, [])

  // Show mode: the tablet drives the conversation, so the screen polls instead
  // of sending. Polling (not a WebSocket) on purpose — a presentation screen
  // refreshing every two seconds is entirely adequate and adds no new failure
  // modes; see docs/PROGRESS.md.
  useEffect(() => {
    if (!showCode) return
    let cancelled = false
    void joinShowSession(showCode).catch(() => undefined)
    const id = setInterval(() => {
      if (!cancelled) void refresh().catch(() => undefined)
    }, SHOW_POLL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [showCode, refresh])

  // Any message activity counts as interaction and pushes the reset back.
  // Intentionally keyed on the message count only — re-running on every store
  // change would restart the timer constantly.
  useEffect(() => {
    if (active) touch()
  }, [messages.length, active])

  if (!active && !showCode) {
    return (
      <button
        onClick={() => void begin()}
        className="flex min-h-screen w-full flex-col items-center justify-center gap-8 bg-bg p-12 text-center"
      >
        <Avatar state="idle" size={280} showLabel={false} />
        <div>
          <h1 className="text-5xl font-semibold">Hallo, ich bin K4ia.</h1>
          <p className="mt-4 text-2xl text-text-muted">
            Tipp mich an oder sprich mich an — ich zeige dir das J4F VisionLab.
          </p>
          <p className="mt-8 text-lg text-text-muted">
            Du sprichst mit einer KI. Es wird kein Bild von dir aufgenommen.
          </p>
        </div>
      </button>
    )
  }

  return (
    <main
      onPointerDown={touch}
      className="flex min-h-screen flex-col items-center justify-between bg-bg p-12"
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-10 text-center">
        <Avatar state={avatarState} size={240} micLevel={micLevel} />

        {lastQuestion && (
          <p className="max-w-4xl text-2xl text-text-muted">„{lastQuestion.content}“</p>
        )}
        <p className="max-w-5xl text-4xl leading-snug" aria-live="polite">
          {lastAnswer?.content || 'Einen Moment …'}
        </p>
      </div>

      <div className="flex w-full items-center justify-between">
        {showCode ? (
          <p className="text-lg text-text-muted">
            Show-Modus · Code <strong className="tracking-widest">{showCode}</strong>
          </p>
        ) : (
          <VoiceControl />
        )}
        {!showCode && (
          <button
            onClick={() => {
              reset()
              setActive(false)
            }}
            className="rounded-xl border border-border px-6 py-3 text-lg"
          >
            Fertig
          </button>
        )}
      </div>
    </main>
  )
}
